import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  ShieldCheck,
  ShieldX,
  Clock,
  FileText,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { apiRequest } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const PRIORITY_TONES = {
  alta: "bg-red-100 text-red-700 border-red-200",
  media: "bg-amber-100 text-amber-800 border-amber-200",
  bassa: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const REVIEW_TONES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AiSuggestions() {
  const { id } = useParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [error, setError] = useState("");
  const [working, setWorking] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams({ auto_drafted_only: "true" });
      if (filter !== "all") qs.set("review_status", filter);
      const data = await apiRequest(`/companies/${id}/ai-mitigation-plans?${qs.toString()}`);
      setPlans(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, filter]);

  const counts = useMemo(() => {
    const c = { pending: 0, accepted: 0, rejected: 0 };
    plans.forEach((p) => {
      c[p.review_status || "pending"] = (c[p.review_status || "pending"] || 0) + 1;
    });
    return c;
  }, [plans]);

  const acceptPlan = async (planId) => {
    setWorking(planId);
    try {
      const r = await apiRequest(
        `/companies/${id}/ai-mitigation-plans/${planId}/accept`,
        { method: "POST" },
      );
      toast.success(`Piano accettato: ${r.actions_created} azioni create nel Piano di Miglioramento`);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWorking(null);
    }
  };

  const rejectPlan = async (planId) => {
    setWorking(planId);
    try {
      await apiRequest(
        `/companies/${id}/ai-mitigation-plans/${planId}/reject`,
        { method: "POST" },
      );
      toast.success("Piano AI scartato");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-suggestions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="outline" data-testid="ai-suggestions-back-button">
          <Link to={`/aziende/${id}`}>
            <ArrowLeft className="h-4 w-4" /> Scheda azienda
          </Link>
        </Button>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">
          <Sparkles className="mr-1 inline h-3 w-3" /> Piani AI Auto-Drafted
        </p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">
          Suggerimenti AI da rivedere
        </h2>
        <p className="mt-2 text-slate-600">
          Piani di mitigazione generati automaticamente dal cron AI (Claude
          Sonnet 4.5 + RAG D.Lgs.81/2008) per i rischi con livello alto o
          significativo. Accettando un piano vengono create automaticamente le
          relative azioni nel Piano di Miglioramento dell'azienda.
        </p>
      </section>

      <div className="flex flex-wrap items-center gap-2">
        {["pending", "accepted", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
              filter === s
                ? "bg-blue-900 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
            data-testid={`ai-filter-${s}`}
          >
            {s === "pending"
              ? `Da rivedere (${counts.pending || 0})`
              : s === "accepted"
              ? `Accettati (${counts.accepted || 0})`
              : s === "rejected"
              ? `Scartati (${counts.rejected || 0})`
              : "Tutti"}
          </button>
        ))}
      </div>

      {error && (
        <Alert variant="destructive" data-testid="ai-suggestions-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-slate-500">
          Caricamento piani AI…
        </p>
      ) : plans.length === 0 ? (
        <Alert className="border-blue-200 bg-blue-50" data-testid="ai-suggestions-empty">
          <FileText className="h-4 w-4 text-blue-700" />
          <AlertTitle>Nessun piano AI {filter !== "all" ? filter : ""}</AlertTitle>
          <AlertDescription>
            Il cron AI Auto-Drafting gira ogni notte alle 02:00 e crea bozze
            solo per i rischi alto/significativo non ancora coperti. Verifica
            di aver compilato le valutazioni di rischio.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          {plans.map((p) => {
            const status = p.review_status || "pending";
            const actions = (p.plan?.actions) || [];
            return (
              <Card key={p.id} className="shadow-sm" data-testid={`ai-plan-card-${p.id}`}>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-2">
                  <div>
                    <CardTitle className="font-heading text-lg">
                      {p.hazard || "Rischio"}
                    </CardTitle>
                    <p className="mt-1 text-xs text-slate-500">
                      <Clock className="mr-1 inline h-3 w-3" />
                      {new Date(p.created_at).toLocaleString("it-IT")} ·{" "}
                      {p.created_by_name || "Sistema"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={REVIEW_TONES[status] || REVIEW_TONES.pending}
                      data-testid={`ai-plan-status-${p.id}`}
                    >
                      {status === "pending"
                        ? "Da rivedere"
                        : status === "accepted"
                        ? "Accettato"
                        : "Scartato"}
                    </Badge>
                    <Badge variant="outline" className="bg-slate-50">
                      {p.risk_level}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {p.plan?.executive_summary && (
                    <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                      <strong>Sintesi:</strong> {p.plan.executive_summary}
                    </p>
                  )}
                  {actions.length > 0 && (
                    <div className="overflow-x-auto rounded-md border border-slate-200">
                      <table className="w-full text-sm">
                        <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                          <tr>
                            <th className="p-3">Azione</th>
                            <th className="p-3">Priorità</th>
                            <th className="p-3">Riferimento</th>
                            <th className="p-3">Tempi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {actions.map((a, i) => (
                            <tr key={i} className="border-b last:border-b-0">
                              <td className="p-3">
                                <p className="font-semibold text-slate-950">{a.title}</p>
                                <p className="mt-1 text-xs text-slate-500">{a.description}</p>
                              </td>
                              <td className="p-3">
                                <Badge
                                  variant="outline"
                                  className={PRIORITY_TONES[a.priority] || "bg-slate-100"}
                                >
                                  {a.priority}
                                </Badge>
                              </td>
                              <td className="p-3 text-xs text-blue-900">
                                {a.legal_reference}
                              </td>
                              <td className="p-3 text-xs">{a.estimated_days}gg</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {p.plan?.residual_risk_after && (
                    <p className="text-xs text-slate-500">
                      Rischio residuo previsto:{" "}
                      <strong>{p.plan.residual_risk_after}</strong>
                    </p>
                  )}

                  {status === "pending" ? (
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        className="bg-emerald-700 hover:bg-emerald-800"
                        onClick={() => acceptPlan(p.id)}
                        disabled={working === p.id}
                        data-testid={`ai-plan-accept-${p.id}`}
                      >
                        <ShieldCheck className="mr-2 h-4 w-4" /> Accetta e crea azioni
                      </Button>
                      <Button
                        variant="outline"
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => rejectPlan(p.id)}
                        disabled={working === p.id}
                        data-testid={`ai-plan-reject-${p.id}`}
                      >
                        <ShieldX className="mr-2 h-4 w-4" /> Scarta
                      </Button>
                    </div>
                  ) : status === "accepted" ? (
                    <Alert className="border-emerald-200 bg-emerald-50">
                      <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                      <AlertTitle>Accettato</AlertTitle>
                      <AlertDescription>
                        {p.review_actions_created || actions.length} azioni create nel
                        Piano di Miglioramento il{" "}
                        {p.review_at &&
                          new Date(p.review_at).toLocaleDateString("it-IT")}{" "}
                        da {p.review_by_name}.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert className="border-slate-200 bg-slate-50">
                      <XCircle className="h-4 w-4 text-slate-600" />
                      <AlertTitle>Scartato</AlertTitle>
                      <AlertDescription>
                        Piano scartato il{" "}
                        {p.review_at &&
                          new Date(p.review_at).toLocaleDateString("it-IT")}{" "}
                        da {p.review_by_name}.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
